import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import PageTitle from "./PageTitle";
import Spacer from "./Spacer";
import Box from "./Box";
import { getParticipantIdFromSearchParams } from '../utils/urlParams';
import StudyCompletionThankYou from './StudyCompletionThankYou';
import './DemographicsPage.styles.css';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

// Age, Education, Income, and Race options as per prompt
const ageOptions = [
  "18-24",
  "25-34",
  "35-44",
  "45-54",
  "55-64",
  "65-74",
  "75 and above",
  "Prefer not to"
];

const educationOptions = [
  "Less than high school degree",
  "High school graduate (high school diploma or equivalent including GED)",
  "Some college but no degree",
  "Associate degree in college (2-year)",
  "Bachelor's degree in college (4-year)",
  "Master's degree",
  "Doctoral degree",
  "Professional degree (JD, MD)",
  "Prefer not to"
];

const incomeOptions = [
  "Less than $10,000",
  "$10,000 to $19,999",
  "$20,000 to $29,999",
  "$30,000 to $39,999",
  "$40,000 to $49,999",
  "$50,000 to $59,999",
  "$60,000 to $69,999",
  "$70,000 to $79,999",
  "$80,000 to $89,999",
  "$90,000 to $99,999",
  "$100,000 to $149,999",
  "$150,000 or more"
];

const raceOptions = [
  "White/Caucasian",
  "Black or African American",
  "American Indian or Alaska Native",
  "Asian",
  "Native Hawaiian or Pacific Islander",
  "Other"
];

const DemographicsPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    age: "",
    gender: "",
    genderOther: "", // Add this line
    education: "",
    income: "",
    race: [] as string[],
    raceOther: ""
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const wasDisconnected = queryParams.get('wasDisconnected') === '1';

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (name === "race") {
      // For checkboxes, handle array of values
      const checked = (e.target as HTMLInputElement).checked;
      setForm((prev) => {
        let newRace = [...prev.race];
        if (checked) {
          newRace.push(value);
        } else {
          newRace = newRace.filter((r) => r !== value);
        }
        return { ...prev, race: newRace };
      });
    } else if (name === "gender" && value !== "other") {
      setForm({ ...form, gender: value, genderOther: "" }); // Reset genderOther if not 'Other'
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const participantId = getParticipantIdFromSearchParams(window.location.search);
    if (!participantId) {
      setError("Participant ID missing from URL. Please use your study link (e.g. ?participantId=... or ?prolificID=...).");
      return;
    }
    // Validation: required fields
    // --- TEST FLAG: set to true to always succeed, false for normal behavior ---
    const ALWAYS_SUCCEED = false;

    if (!ALWAYS_SUCCEED) {
      if (
        !form.age ||
        !form.gender ||
        !form.education ||
        !form.income ||
        form.race.length === 0 ||
        (form.race.includes("Other") && !form.raceOther.trim())
      ) {
        setError("Please complete all required fields.");
        return;
      }
      try {
        const raceValue = form.race.includes("Other")
          ? [...form.race.filter(r => r !== "Other"), form.raceOther]
          : form.race;

        const raceString = Array.isArray(raceValue) ? raceValue.join(', ') : raceValue;

        const res = await fetch(`${API_BASE_URL}/demographics`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'prolific-id': participantId
          },
          body: JSON.stringify({
            age: form.age,
            gender: form.gender,
            education: form.education,
            income: form.income,
            race: raceString
          })
        });
        if (!res.ok) {
          const data = await res.json();
          // Safely convert error to string, handling cases where it might be an object
          const errorText = data.error;
          const errorString = typeof errorText === 'string' ? errorText : JSON.stringify(errorText);
          setError(errorString || 'Failed to submit demographics.');
          return;
        }
        setSubmitted(true);
        if (wasDisconnected) {
          setTimeout(() => navigate('/participation-acknowledged'), 1000);
        }
      } catch (err) {
        setError('Failed to submit demographics.');
      }
    } else {
      // Always succeed for testing
      setSubmitted(true);
      if (wasDisconnected) {
        setTimeout(() => navigate('/participation-acknowledged'), 1000);
      }
    }
  }

  const participantId = getParticipantIdFromSearchParams(window.location.search);

  if (submitted && !wasDisconnected) {
    return <StudyCompletionThankYou participantId={participantId} />;
  }

  if (submitted && wasDisconnected) {
    return (
      <div className="demographics-bg">
        <div className="demographics-card fade-in">
          <div className="demographics-thankyou">Thank you! Redirecting…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="demographics-bg">
      <div className="demographics-card fade-in">
        <PageTitle title="Demographic Information" />
        <Spacer />
        <form onSubmit={handleSubmit} className="demographics-form">
          {error && <div className="demographics-error">{error}</div>}

          {/* Age */}
          <div className="demographics-field">
            <label>
              <span className="demographics-label">
                What is your age? <span className="required">*</span>
              </span>
              <div className="demographics-input-icon">
                <select
                  name="age"
                  value={form.age}
                  onChange={handleChange}
                  required
                  className="demographics-input"
                >
                  <option value="">Select</option>
                  {ageOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="input-icon" role="img" aria-label="age">🎂</span>
              </div>
            </label>
          </div>

          {/* Gender (unchanged) */}
          <div className="demographics-field">
            <label>
              <span className="demographics-label">Gender <span className="required">*</span></span>
              <div className="demographics-input-icon">
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  required
                  className="demographics-input"
                >
                  <option value="">Select</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="nonbinary">Non-binary</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                  <option value="other">Other</option>
                </select>
                <span className="input-icon" role="img" aria-label="gender">⚧️</span>
              </div>
              {form.gender === "other" && (
                <input
                  type="text"
                  name="genderOther"
                  value={form.genderOther}
                  onChange={handleChange}
                  required
                  className="demographics-input"
                  placeholder="Please specify your gender"
                  style={{ marginTop: 8, maxWidth: 320 }}
                />
              )}
            </label>
          </div>

          {/* Education */}
          <div className="demographics-field">
            <label>
              <span className="demographics-label">
                What is the highest level of school you have completed or the highest degree you have received? <span className="required">*</span>
              </span>
              <div className="demographics-input-icon">
                <select
                  name="education"
                  value={form.education}
                  onChange={handleChange}
                  required
                  className="demographics-input"
                >
                  <option value="">Select</option>
                  {educationOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="input-icon" role="img" aria-label="education">🎓</span>
              </div>
            </label>
          </div>

          {/* Income */}
          <div className="demographics-field">
            <label>
              <span className="demographics-label">
                Information about income is very important to understand. Would you please give your best guess? <br />
                <span style={{ fontWeight: 400, fontSize: "0.97em" }}>
                  Please indicate the answer that includes your entire household income in (previous year) before taxes.
                </span>
                <span className="required">*</span>
              </span>
              <div className="demographics-input-icon">
                <select
                  name="income"
                  value={form.income}
                  onChange={handleChange}
                  required
                  className="demographics-input"
                >
                  <option value="">Select</option>
                  {incomeOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className="input-icon" role="img" aria-label="income">💰</span>
              </div>
            </label>
          </div>

          {/* Race/Ethnicity (multi-select) */}
          <div className="demographics-field">
            <label>
              <span className="demographics-label">
                Please choose whichever ethnicity that you identify with (you may choose more than one option): <span className="required">*</span>
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
                {raceOptions.map(opt => (
                  <label key={opt} style={{ display: "flex", alignItems: "center", gap: "7px", fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      name="race"
                      value={opt}
                      checked={form.race.includes(opt)}
                      onChange={handleChange}
                      style={{ accentColor: "#1976d2" }}
                      required={form.race.length === 0}
                    />
                    {opt}
                  </label>
                ))}
                {form.race.includes("Other") && (
                  <input
                    type="text"
                    name="raceOther"
                    value={form.raceOther}
                    onChange={handleChange}
                    required
                    className="demographics-input"
                    placeholder="Please specify your ethnicity"
                    style={{ marginTop: 8, maxWidth: 320 }}
                  />
                )}
              </div>
            </label>
          </div>

          <button
            type="submit"
            className="demographics-submit"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default DemographicsPage;